import { Link, useParams } from '@tanstack/react-router'

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  cn,
} from '@hap/components'

import { useImportRecordingsPicker, useUploadContext } from './hooks'
import { UploadStatus } from './types'

const statusLabels: Record<UploadStatus, string> = {
  pending: 'Pending',
  uploading: 'Uploading',
  uploaded: 'Uploaded',
  importing: 'Importing',
  transcribing: 'Transcribing',
  analyzing: 'Analyzing',
  done: 'Complete',
  error: 'Failed',
}

const circleRadius = 13
const circleCircumference = 2 * Math.PI * circleRadius

interface ImportProgressDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ImportProgressDialog({
  open,
  onOpenChange,
}: ImportProgressDialogProps) {
  const { items, importProgress, isComplete, hasErrors } = useUploadContext()
  const { alias } = useParams({ from: '/_authenticated/$alias/_app' })
  const importRecordings = useImportRecordingsPicker()

  const activeItems = items.filter(
    (item) => item.status !== 'done' && item.status !== 'error',
  )
  const processingCount = activeItems.length
  const failedCount = items.filter((item) => item.status === 'error').length
  const progressOffset =
    circleCircumference - (importProgress.percent / 100) * circleCircumference
  const interactionLabel = processingCount === 1 ? 'interaction' : 'interactions'
  const title = hasErrors
    ? `${failedCount} ${failedCount === 1 ? 'interaction has' : 'interactions have'} errors`
    : isComplete
      ? 'Imports complete'
      : `${processingCount} ${interactionLabel} processing`

  const getStatusIcon = (status: UploadStatus) => {
    if (status === 'done') return 'check'
    if (status === 'error') return 'x'
    return 'arrows-clockwise'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] p-0">
        <DialogHeader className="border-b px-6 py-5">
          <DialogTitle>Import progress</DialogTitle>
          <DialogDescription>{title}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5 px-6 py-5">
          <div className="outline-celestial/20 bg-celestial/10 rounded-xl p-4 outline-2">
            <div className="mb-2 flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">{importProgress.percent}% complete</div>
                <div className="text-muted-foreground text-sm">
                  {importProgress.completed}/{importProgress.total} interactions finished
                </div>
              </div>
              <div className="relative size-12 shrink-0">
                <svg className="absolute inset-0 size-12 -rotate-90" viewBox="0 0 32 32">
                  <circle
                    cx="16"
                    cy="16"
                    r={circleRadius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    className="text-background"
                  />
                  <circle
                    cx="16"
                    cy="16"
                    r={circleRadius}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeDasharray={circleCircumference}
                    strokeDashoffset={progressOffset}
                    className={cn(
                      'text-celestial transition-all duration-500',
                      hasErrors && 'text-destructive',
                    )}
                  />
                </svg>
                <span className="flex size-12 items-center justify-center text-sm font-medium">
                  {importProgress.percent}
                </span>
              </div>
            </div>
            <div className="bg-background/70 h-2 overflow-hidden rounded-full">
              <div
                className={cn(
                  'bg-celestial h-full rounded-full transition-all duration-500',
                  hasErrors && 'bg-destructive',
                )}
                style={{ width: `${importProgress.percent}%` }}
              />
            </div>
          </div>

          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {items.map((item) => (
              <div
                key={item.id}
                className="border-border/70 flex items-center gap-3 rounded-lg border p-3"
              >
                <Icon
                  name={getStatusIcon(item.status)}
                  className={cn(
                    'size-4 shrink-0 text-celestial',
                    item.status !== 'done' && item.status !== 'error' && 'animate-spin',
                    item.status === 'done' && 'text-green-600',
                    item.status === 'error' && 'text-destructive',
                  )}
                />
                <div className="min-w-0 grow">
                  <div className="truncate text-sm font-medium">{item.fileName}</div>
                  {item.error && (
                    <div className="text-destructive truncate text-xs">{item.error}</div>
                  )}
                </div>
                <span className="text-muted-foreground shrink-0 text-xs">
                  {statusLabels[item.status]}
                </span>
                {item.contactLogId && (
                  <Button variant="ghost" size="sm" asChild>
                    <Link
                      to="/$alias/interaction/$contactLogId"
                      params={{ alias, contactLogId: item.contactLogId }}
                      onClick={() => onOpenChange(false)}
                    >
                      View
                    </Link>
                  </Button>
                )}
              </div>
            ))}
          </div>
        </DialogBody>

        <DialogFooter className="border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button icon="upload-simple" onClick={importRecordings}>
            Import more recordings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
